import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import type { CoinPosition, PegPosition } from "../types/schema";

interface DroppingCoinProps {
  startX: number;
  startY: number;
  pegs: PegPosition[];
  onLanded: (slotIndex: number) => void;
  slotWidth: number;
  slotCount: number;
  boardWidth: number;
  targetSlot?: number;
}

export const DroppingCoinDesktop = ({
  startX,
  startY,
  pegs,
  onLanded,
  slotWidth,
  slotCount,
  boardWidth,
  targetSlot,
}: DroppingCoinProps) => {
  const [position, setPosition] = useState<CoinPosition>({
    x: startX,
    y: startY,
  });
  const velocityRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const gravity = 1.2;
    const bounce = 0.36;
    const friction = 0.97;
    const coinRadius = 14;
    const pegRadius = 10;
    const maxVelocity = 11;

    const animate = () => {
      setPosition((prev) => {
        let newX = prev.x + velocityRef.current.x;
        let newY = prev.y + velocityRef.current.y;

        // Apply gravity
        velocityRef.current.y += gravity;

        // Apply friction and air resistance
        velocityRef.current.x *= friction;
        velocityRef.current.y *= 0.995;

        // Limit maximum velocity for more natural movement
        const speed = Math.sqrt(
          velocityRef.current.x * velocityRef.current.x +
            velocityRef.current.y * velocityRef.current.y
        );
        if (speed > maxVelocity) {
          velocityRef.current.x = (velocityRef.current.x / speed) * maxVelocity;
          velocityRef.current.y = (velocityRef.current.y / speed) * maxVelocity;
        }

        // Check collision with pegs
        pegs.forEach((peg) => {
          const dx = newX - peg.x;
          const dy = newY - peg.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < coinRadius + pegRadius) {
            // Collision detected
            const angle = Math.atan2(dy, dx);
            const targetX = peg.x + Math.cos(angle) * (coinRadius + pegRadius);
            const targetY = peg.y + Math.sin(angle) * (coinRadius + pegRadius);

            // More natural bounce calculation
            const normalX = dx / distance;
            const normalY = dy / distance;

            const dotProduct =
              velocityRef.current.x * normalX + velocityRef.current.y * normalY;

            velocityRef.current.x =
              (velocityRef.current.x - 2 * dotProduct * normalX) * bounce;
            velocityRef.current.y =
              (velocityRef.current.y - 2 * dotProduct * normalY) * bounce;

            // Add slight random horizontal velocity for variety
            velocityRef.current.x += (Math.random() - 0.5) * 1.2;

            // Add slight damping on collision
            velocityRef.current.x *= 0.98;
            velocityRef.current.y *= 0.98;

            newX = targetX;
            newY = targetY;
          }
        });

        // Boundary checks
        if (newX < coinRadius) {
          newX = coinRadius;
          velocityRef.current.x *= -bounce;
        }
        if (newX > boardWidth - coinRadius) {
          newX = boardWidth - coinRadius;
          velocityRef.current.x *= -bounce;
        }

        // Apply gentle guidance towards target slot if specified
        if (targetSlot !== undefined && newY > 100) {
          const targetX = (targetSlot + 0.5) * (boardWidth / slotCount);
          const distanceToTarget = targetX - newX;
          const guidanceForce = distanceToTarget * 0.002;
          velocityRef.current.x += guidanceForce;
        }

        // Check if landed in slot (bottom of board)
        if (newY > 580) {
          let slotIndex = Math.floor((newX / boardWidth) * slotCount);

          // If target slot specified, ensure we land there
          if (targetSlot !== undefined) {
            slotIndex = targetSlot;
          }

          const clampedIndex = Math.max(0, Math.min(slotCount - 1, slotIndex));
          onLanded(clampedIndex);
          return prev; // Stop animation
        }

        return { x: newX, y: newY };
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [pegs, onLanded, slotWidth, slotCount, boardWidth]);

  return (
    <Box
      component="img"
      src="/coin-gold.png"
      sx={(theme) => ({
        position: "absolute",
        left: position.x - 14,
        top: position.y - 100,
        width: 28,
        height: 28,
        backgroundSize: "contain",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        pointerEvents: "none",
        zIndex: 10,
        transition: "transform 0.1s ease-out",
        transform: `rotate(${position.x * 2}deg)`,

        // RESPONSIVE: prilagodba za mobitele
        [theme.breakpoints.down("sm")]: {
          width: 20,
          height: 20,
          left: position.x - 10,
          top: position.y - 70,
        },
      })}
    />
  );
};
